import { useEffect, useState } from "react";

import { Destination } from "common/router/types";
import { useNavigate } from "common/router/hooks";
import { Optional } from "common/utils/types";
import { UISubscriptionStatus } from "common/types";

import { useClientQuery } from "client/accounts/hooks";
import ProgressBar from "client/common/components/display/ProgressBar";
import Error404Page from "client/common/components/errors/Error404Page";
import { urls } from "client/routes";

import {
  OnboardingSubscriptionsStep,
  OnboardingSubscriptionsStepParams,
  OnboardingDetailSubscriptionStep,
  IOnboardingSubscription,
} from "../types";
import OnboardingSubscriptionsBillingStep from "./steps/OnboardingSubscriptionsBillingStep";
import OnboardingSubscriptionsCompleteStep from "./steps/OnboardingSubscriptionsCompleteStep";
import { inferNextStep, inferPreviousStep, calcTotalSteps, tryList } from "../utils";
import { useOnboardingQuery } from "../hooks";
import OnboardingSubscriptionsListStep from "./steps/OnboardingSubscriptionsListStep";
import OnboardingSubscriptionsStepPage from "./OnboardingSubscriptionsStepPage";
import OnboardingSubscriptionStep from "./steps/OnboardingSubscriptionStep";
import OnboardingSubscriptionsProfileMissingFieldsStep from "./steps/OnboardingSubscriptionsProfileMissingFieldsStep";
import OnboardingSubscriptionsControlledSubstancesStep from "./steps/OnboardingSubscriptionsControlledSubstancesStep";

type StepDefinition = {
  body: (props: OnboardingSubscriptionsStepParams) => React.ReactElement;
  dest: (props: { id: Optional<number>; subscriptions: IOnboardingSubscription[] }) => Destination;
  stepIndex: number;
};

const subscriptionsRoot = urls.onboarding.subscriptions;

const stepToDefinition: { [K in OnboardingSubscriptionsStep]: StepDefinition } = {
  [OnboardingSubscriptionsStep.List]: {
    body: (props) => <OnboardingSubscriptionsListStep {...props} />,
    dest: () => subscriptionsRoot.start(),
    stepIndex: 0,
  },
  [OnboardingSubscriptionsStep.QuantityFrequency]: {
    body: (props) => (
      <OnboardingSubscriptionStep
        step={OnboardingDetailSubscriptionStep.QuantityFrequency}
        {...props}
      />
    ),
    dest: ({ id }) =>
      subscriptionsRoot.detail({
        params: {
          id: id ?? 0,
          step: OnboardingSubscriptionsStep.QuantityFrequency,
        },
      }),
    stepIndex: 0,
  },
  [OnboardingSubscriptionsStep.NextOrderDate]: {
    body: (props) => (
      <OnboardingSubscriptionStep
        step={OnboardingDetailSubscriptionStep.NextOrderDate}
        {...props}
      />
    ),
    dest: ({ id }) =>
      subscriptionsRoot.detail({
        params: {
          id: id ?? 0,
          step: OnboardingSubscriptionsStep.NextOrderDate,
        },
      }),
    stepIndex: 0,
  },
  [OnboardingSubscriptionsStep.Cancel]: {
    body: (props) => (
      <OnboardingSubscriptionStep step={OnboardingDetailSubscriptionStep.Cancel} {...props} />
    ),
    dest: ({ id }) =>
      subscriptionsRoot.detail({
        params: {
          id: id ?? 0,
          step: OnboardingSubscriptionsStep.Cancel,
        },
      }),
    stepIndex: 0,
  },
  [OnboardingSubscriptionsStep.Billing]: {
    body: (props) => <OnboardingSubscriptionsBillingStep {...props} />,
    dest: () => subscriptionsRoot.list({ params: { step: OnboardingSubscriptionsStep.Billing } }),
    stepIndex: 1,
  },
  [OnboardingSubscriptionsStep.ProfileMissingFields]: {
    body: (props) => <OnboardingSubscriptionsProfileMissingFieldsStep {...props} />,
    dest: () =>
      subscriptionsRoot.list({
        params: { step: OnboardingSubscriptionsStep.ProfileMissingFields },
      }),
    stepIndex: 1,
  },
  [OnboardingSubscriptionsStep.PatientMissingFields]: {
    body: (props) => (
      <OnboardingSubscriptionStep
        step={OnboardingDetailSubscriptionStep.PatientMissingFields}
        {...props}
      />
    ),
    dest: ({ subscriptions }) => {
      // grab first subscription that meets criteria. once it's updated, we will invalidate and
      // it won't meet criteria anymore so we move on to the next one
      const { id } = subscriptions.filter(
        (sub) =>
          sub.visible_status !== UISubscriptionStatus.Canceled &&
          sub.patient.missing_checkout_fields.length,
      )[0];
      return subscriptionsRoot.detail({
        params: { id: id ?? 0, step: OnboardingSubscriptionsStep.PatientMissingFields },
      });
    },
    stepIndex: 1,
  },
  [OnboardingSubscriptionsStep.ControlledSubstances]: {
    body: (props) => <OnboardingSubscriptionsControlledSubstancesStep {...props} />,
    dest: () =>
      subscriptionsRoot.list({
        params: { step: OnboardingSubscriptionsStep.ControlledSubstances },
      }),
    stepIndex: 1,
  },
  [OnboardingSubscriptionsStep.Complete]: {
    body: (props) => <OnboardingSubscriptionsCompleteStep {...props} />,
    dest: () => subscriptionsRoot.list({ params: { step: OnboardingSubscriptionsStep.Complete } }),
    stepIndex: 2,
  },
};

type OnboardingSubscriptionsContentProps = {
  id: Optional<number>;
  step: OnboardingSubscriptionsStep;
};

const OnboardingSubscriptionsContent = ({ id, step }: OnboardingSubscriptionsContentProps) => {
  const navigate = useNavigate();
  const [shouldGoToNext, setShouldGoToNext] = useState(false);
  const [shouldGoToPrevious, setShouldGoToPrevious] = useState(false);
  const {
    data: client,
    isLoading: isClientLoading,
    isFetching: isClientFetching,
    error: clientError,
  } = useClientQuery();
  const {
    data: onboarding,
    isLoading: isOnboardingLoading,
    isFetching: isOnboardingFetching,
    error: onboardingError,
  } = useOnboardingQuery();

  const isFetching = isClientFetching || isOnboardingFetching;

  useEffect(() => {
    // instead of navigating directly onNext or onPrevious, ensure that we have finished refetching the relevant data.
    // the client or onboarding data may have changed in a way that impacts the inferred next or previous step
    if (isFetching || !onboarding || !client) {
      return;
    }
    if (shouldGoToNext) {
      const inferredNextStep = inferNextStep({
        prevStep: step,
        onboarding,
        client,
      });
      if (inferredNextStep) {
        navigate(
          stepToDefinition[inferredNextStep].dest({ id, subscriptions: onboarding.subscriptions }),
        );
        setShouldGoToNext(false);
      }
    } else if (shouldGoToPrevious) {
      const inferredPreviousStep = inferPreviousStep(step);
      if (inferredPreviousStep) {
        navigate(
          stepToDefinition[inferredPreviousStep].dest({
            id,
            subscriptions: onboarding.subscriptions,
          }),
        );
        setShouldGoToPrevious(false);
      }
    }
  }, [isFetching, shouldGoToNext, step, onboarding, client, id, navigate, shouldGoToPrevious]);

  if (isClientLoading || isOnboardingLoading) {
    return <OnboardingSubscriptionsStepPage.Loading />;
  }

  const error = clientError || onboardingError;
  if (!client || !onboarding || error) {
    return (
      <OnboardingSubscriptionsStepPage.RedirectInvalid
        dest={subscriptionsRoot.error({ search: { status: error?.status } })}
      />
    );
  }

  const currDefinition = step && stepToDefinition[step];
  if (!currDefinition) {
    return <Error404Page />;
  }
  const { body, stepIndex } = currDefinition;

  const stepper = (
    <ProgressBar totalSteps={calcTotalSteps(stepToDefinition)} stepIndex={stepIndex} />
  );
  const rootDest = stepToDefinition[tryList({ onboarding, client })].dest({
    id,
    subscriptions: onboarding.subscriptions,
  });
  const subscription = onboarding.subscriptions.find((s) => s.id === id);

  return (
    <OnboardingSubscriptionsStepPage onPrevious={() => setShouldGoToPrevious(true)}>
      {body({
        client,
        onboarding,
        onNext: () => setShouldGoToNext(true),
        isLoadingNext: shouldGoToNext && isFetching,
        rootDest,
        stepper,
        id,
        subscription,
      })}
    </OnboardingSubscriptionsStepPage>
  );
};

export default OnboardingSubscriptionsContent;
